import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { ArrowLeft } from 'lucide-react';
import ServiceConnectorCards from './ServiceConnector';
import { getStatus } from '../../services/integrationService';
import { getAllConnectionStatuses } from '../../services/oauthService';
import LanguageSwitcher from '../LanguageSwitcher';
import { useLanguage } from '../../hooks/useLanguage';

// ─── Step constants ──────────────────────────────────────────────────────────
const SCREEN = {
  ADMIN_ORG:   'admin_org',     // Admin: enter org name
  ADMIN_CONNECT: 'admin_connect', // Admin: connect services
  USER_JOIN:   'user_join',     // User: select org & request access
  USER_PENDING: 'user_pending', // User: waiting for admin approval
  CONFIGURED:  'configured',   // Already set up — show status
};

// WORKSPACE DISABLED — restore with org/admin handlers
// const getKeycloakRole = (keycloak, adminRoleName = 'admin') => { ... };

// ─── Helper: connected-service badge (WORKSPACE DISABLED — unused for now)
// const ServiceBadge = ({ name, connected }) => { ... };

// ─── Main Component ───────────────────────────────────────────────────────────
const IntegrationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const [searchParams] = useSearchParams();

  const [currentLanguage, setLanguage] = useLanguage();
  const [screen, setScreen] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState(null); // kept for bootstrap; used when workspace UI is restored

  // WORKSPACE DISABLED — state for org naming / join flow (restore with handlers below)
  // const [isAdmin, setIsAdmin] = useState(false);
  // const [orgName, setOrgName] = useState('');
  // const [adminTenants, setAdminTenants] = useState([]);
  // ...

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    bootstrap();
  }, []);

  useEffect(() => {
    if (searchParams.get('start') === 'fresh') {
      // Re-run bootstrap so the user lands on the correct role-based screen
      setStatus(null);
      navigate('/integrations', { replace: true });
      bootstrap();
    }
  }, [searchParams]);

  const bootstrap = async ({ forceRefresh = false } = {}) => {
    try {
      setIsLoading(true);
      const email = keycloak.tokenParsed?.email;
      if (!email) { setIsLoading(false); return; }

      // ── Determine role from Keycloak JWT (mirrors backend is_admin_token()) ──
      const statusData = await getStatus(email, keycloak.token, { forceRefresh }).catch(() => null);

      if (statusData) {
        setStatus(statusData);

        try {
          await getAllConnectionStatuses(keycloak.token);
        } catch (_) { /* ignore */ }

        // All users (admin and regular) go to configured screen
        // Regular users can now set up their own integrations
        setScreen(SCREEN.CONFIGURED);
      } else {
        // WORKSPACE DISABLED — skip org naming; go straight to service connectors
        // if (isKeycloakAdmin) {
        //   setScreen(SCREEN.ADMIN_ORG);
        // } else {
        //   window.location.reload();
        // }
        setScreen(SCREEN.ADMIN_CONNECT);
      }
    } catch (err) {
      console.error('Bootstrap error:', err);
      // WORKSPACE DISABLED — setScreen(isKeycloakAdmin ? SCREEN.ADMIN_ORG : SCREEN.USER_JOIN);
      setScreen(SCREEN.ADMIN_CONNECT);
    } finally {
      setIsLoading(false);
    }
  };


  // WORKSPACE DISABLED — restore when org naming / deletion is needed again
  // const handleOrgSave = async () => { ... };
  // const handleDeleteOrg = async () => { ... };
  // const handleJoinRequest = async () => { ... };
  // const handleSwitchOrg = async () => { ... };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/dashboards')}
              className="mr-4 flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft size={16} className="mr-2" />
              {t('common.backToDashboard')}
            </button>
            <h1 className="text-2xl font-bold text-gray-900">{t('integrations.title')}</h1>
          </div>
          <LanguageSwitcher
            currentLanguage={currentLanguage}
            onLanguageChange={setLanguage}
          />
        </div>

        {/* WORKSPACE DISABLED — restore when org naming is needed again
        {screen === SCREEN.ADMIN_ORG && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-lg mx-auto">
            ...
          </div>
        )}
        */}

        {/* ── SCREEN: Connect Services ───────────────────────────────────── */}
        {screen === SCREEN.ADMIN_CONNECT && (
          <div className="space-y-4">
            {/* WORKSPACE DISABLED — org name header
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4 flex items-center gap-3">
              ...
            </div>
            */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <p className="text-sm text-gray-500 mb-4">
                {t('integrations.connectServicesDescription', {
                  defaultValue: 'Connect your services to enable data integration and research capabilities.',
                })}
              </p>
              <ServiceConnectorCards />
            </div>
          </div>
        )}

        {/* WORKSPACE DISABLED — join-org flow
        {screen === SCREEN.USER_JOIN && ( ... )}
        {screen === SCREEN.USER_PENDING && ( ... )}
        */}

        {/* ── SCREEN: Configured ──────────────────────────────────────────── */}
        {screen === SCREEN.CONFIGURED && (
          <div className="space-y-5">
            {/* WORKSPACE DISABLED — org bar + delete button
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4 flex items-center justify-between">
              ...
            </div>
            */}

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <p className="text-sm text-gray-500 mb-4">
                {t('integrations.connectServicesDescription', {
                  defaultValue: 'Connect your services to enable data integration and research capabilities.',
                })}
              </p>
              <ServiceConnectorCards />
            </div>
          </div>
        )}
      </div>

      {/* WORKSPACE DISABLED — AccessRequestModal for join-org flow
      <AccessRequestModal ... />
      */}
    </div>
  );
};

export default IntegrationsPage;